import {getActionResponse, responseToData, transferify, unRestangularize} from 'shared/utils/data-service-utils';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {
    ActionResponse,
    Auth,
    FinishedActionResponse,
    FinishedSaveResponse,
    RelationsRequest,
    User,
    UserDetailManagerDto,
    UserEditationRequest
} from 'typings/portaro.be.types';
import CachedPermissionsDataService from '../commons/data-services/cached-permissions.data-service';
import type {IHttpService} from 'angular';
import type {AnyObject} from 'typings/portaro.fe.types';

export default class UserDataService {
    public static readonly serviceName = 'userDataService';

    public static readonly USERS_ROUTE = 'users';

    /*@ngInject*/
    constructor(private Restangular: Restangular.IService,
                private currentAuth: Auth,
                private $http: IHttpService,
                private CONTEXT_PATH: string) {
    }

    @ngAsync()
    @unRestangularize()
    public async saveUser(userEditationRequest: UserEditationRequest): Promise<FinishedSaveResponse<User>> {
        try {
            const response = await this.Restangular
                .all(UserDataService.USERS_ROUTE)
                .post(transferify(userEditationRequest));
            CachedPermissionsDataService.clearCache();
            return response;
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async editUserByForm(userId: number): Promise<FinishedSaveResponse<User>> {
        try {
            const response = await this.Restangular
                .one(UserDataService.USERS_ROUTE, userId)
                .one('form')
                .get();
            CachedPermissionsDataService.clearCache();
            return response;
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async getUser(userId: number): Promise<User> {
        try {
            return await this.Restangular
                .one(UserDataService.USERS_ROUTE, userId)
                .get();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    /**
     * @description
     *
     * @param {number} userId
     * @returns {Promise<ActionResponse>}
     */
    @ngAsync()
    public async removeUser(userId: number): Promise<ActionResponse> {
        try {
            const httpResponse = await this.$http.delete<ActionResponse>(`${this.CONTEXT_PATH}/api/${UserDataService.USERS_ROUTE}/${userId}`, {
                data: {user: userId},
                headers: {'Content-Type': 'application/json'}
            });
            CachedPermissionsDataService.clearCache();
            return httpResponse.data;
        } catch (httpError) {
            throw getActionResponse(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async getUserViewManager(userId: number): Promise<UserDetailManagerDto> {
        try {
            return await this.Restangular
                .one(UserDataService.USERS_ROUTE, userId)
                .one('detail-view-settings')
                .get();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async extendRegistration(userId: number): Promise<FinishedActionResponse> {
        try {
            const response = await this.Restangular
                .one(UserDataService.USERS_ROUTE, userId)
                .all('registration-extend')
                .post({extendingUser: userId, currentUser: this.currentAuth.activeUser.id});
            CachedPermissionsDataService.clearCache();
            return response;
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    /**
     * @param {number} userId
     * @returns {Promise<any>}
     */
    @ngAsync()
    @unRestangularize()
    public async deleteUserCredentials(userId: number) {
        try {
            const response = await this.Restangular
                .one(UserDataService.USERS_ROUTE, userId)
                .one('credentials-delete')
                .get();
            CachedPermissionsDataService.clearCache();
            return response;
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async changePassword(formObject): Promise<FinishedActionResponse> {
        try {
            return await this.Restangular
                .one(UserDataService.USERS_ROUTE)
                .one('password-change')
                .post(null, transferify(formObject));
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async changePasswordMail(): Promise<FinishedActionResponse> {
        try {
            return await this.Restangular
                .all(UserDataService.USERS_ROUTE)
                .all('tokened-password-change-mail')
                .post({});
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async getDelayedReturnPenalty(userId: number): Promise<DelayedReturnPenalty> {
        try {
            return await this.Restangular
                .one(UserDataService.USERS_ROUTE, userId)
                .one('delayed-return-penalty')
                .get();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    public async getCardNumberSequence(departmentsIds: number[]): Promise<string> {
        try {
            return await this.Restangular
                .all(UserDataService.USERS_ROUTE)
                .one('sequences', 'card-number')
                .get({departments: departmentsIds});
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    public async getReaderBarCodeSequence(departmentId: number): Promise<string> {
        try {
            return await this.Restangular
                .all(UserDataService.USERS_ROUTE)
                .one('sequences', 'bar-code')
                .get({department: departmentId});
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    public async saveRelations(relationsRequest: Partial<RelationsRequest>): Promise<FinishedActionResponse> {
        try {
            return await this.Restangular
                .all(UserDataService.USERS_ROUTE)
                .all('relations')
                .post(transferify(relationsRequest));
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    public async step0registration(request: AnyObject): Promise<ActionResponse> {
        try {
            return await this.Restangular
                .all(UserDataService.USERS_ROUTE)
                .all('credentials-registration')
                .all('first-login')
                .post(request);
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }
}

export interface DelayedReturnPenalty {
    penalizedLoansCount: number;
    sum: number;
}
